import { Button, Stack } from '@mui/material';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useInterestsStore } from '../../stores/interests';
import { Container, StyledPaper } from './ProfileInteresets.style';
import InterestCard from 'components/InterestCard/InterestCard';
import { useTranslations, useResponsive, useJsonApi, useSnackbar } from 'hooks';

import { FormProvider } from 'providers';
import { useAuthSelector } from 'stores';
import { useGlobalStore } from 'stores';
import { IInterest } from 'types/stores';

// Maps Drupal taxonomy term names to display label and icon.
// TIDs come from Drupal at runtime so there is no environment-specific mismatch.
const nameToIcon: Record<string, { displayName: string; iconType: string }> = {
  'Academy': { displayName: 'Academy', iconType: 'formazione' },
  'Centro Studi': { displayName: 'Centro Studi', iconType: 'centro_studi' },
  'Finanza': { displayName: 'Finanza', iconType: 'finanziamenti_fisco' },
  'Proprietà intellettuale': { displayName: 'Proprietà\nintellettuale', iconType: 'proprieta_intellettuale' },
  'Libreria Materiali': { displayName: 'Libreria Materiali', iconType: 'libreria_materiali' },
  'TECLA': { displayName: 'TECLA', iconType: 'TECLA' },
  'Survey Generale': { displayName: 'Survey Generale', iconType: 'survey' },
  'Bosco del Design': { displayName: 'Bosco del Design', iconType: 'bosco_design' },
  'Portale Certificazioni': { displayName: 'Portale\nCertificazioni', iconType: 'portale_certificazioni' },
  'Sportello Doganale': { displayName: 'Sportello\nDoganale', iconType: 'sportello_doganale' },
};

const ProfileInterests = () => {
  const isMobile = useResponsive('down', 'md');
  const { user, storeUser } = useAuthSelector();
  const [interestCards, setInterestCards] = useState<IInterest[]>([]);
  const [cardsSelected, setCardsSelected] = useState<IInterest[]>([]);
  const [tipoServizioCards, setTipoServizioCards] = useState<IInterest[]>([]);
  const [tipoServizioSelected, setTipoServizioSelected] = useState<IInterest[]>([]);
  const [associazioneCards, setAssociazioneCards] = useState<IInterest[]>([]);
  const [associazioneSelected, setAssociazioneSelected] = useState<IInterest[]>([]);
  const methods = useForm();
  const { handleSubmit } = methods;
  const i18n = useTranslations();
  const { showLoader, hideLoader } = useGlobalStore();
  const { updateInterests, listEntities } = useJsonApi({ showLoader, hideLoader });
  const { errorSnackbar, successSnackbar } = useSnackbar();

  const onSetInterests = async (interest: IInterest) => {
    setCardsSelected(prev => [...prev, interest]);
  };

  const onRemoveInterests = (interest: IInterest) => {
    setCardsSelected(prev =>
      prev.filter(i => i.interestType !== interest.interestType)
    );
  };

  const onSubmit = async () => {
    try {
      showLoader();
      const terms = [
        ...cardsSelected
          .filter(c => c.uuid)
          .map(c => ({
            type: 'taxonomy_term--interests',
            id: c.uuid as string,
          })),
        ...tipoServizioSelected
          .filter(c => c.uuid)
          .map(c => ({
            type: 'taxonomy_term--tipo_servizio',
            id: c.uuid as string,
          })),
        ...associazioneSelected
          .filter(c => c.uuid)
          .map(c => ({
            type: 'taxonomy_term--associazione',
            id: c.uuid as string,
          })),
      ];
      const res = await updateInterests({
        userUuid: user?.uuid as string,
        terms,
      });
      if (!res) throw new Error();
      useInterestsStore.setState({ interests: cardsSelected });
      successSnackbar(i18n.general.snackbar.success);
      const allTids = [
        ...cardsSelected.map(c => `${c.drupalId}`),
        ...tipoServizioSelected.map(c => `${c.drupalId}`),
        ...associazioneSelected.map(c => `${c.drupalId}`),
      ];
      user && storeUser({ ...user, field_interests: allTids });
    } catch (error) {
      errorSnackbar(i18n.general.errors.generic);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const fetchInterests = async () => {
      const res = await listEntities({
        body: {
          entity: 'taxonomy_term',
          bundle: 'interests',
          deserializeEntities: true,
          disableStatusFilter: true,
          specificResValue: ['id', 'drupal_internal__tid', 'name'],
        },
      });
      if (res) {
        const drupalTerms = res as unknown as {
          id: string;
          drupal_internal__tid: number;
          name: string;
        }[];
        const cards: IInterest[] = drupalTerms
          .filter(t => nameToIcon[t.name])
          .map(t => ({
            interestType: nameToIcon[t.name].displayName,
            iconType: nameToIcon[t.name].iconType,
            drupalId: t.drupal_internal__tid,
            uuid: t.id,
          }));
        setInterestCards(cards);
        if (user?.field_interests) {
          setCardsSelected(
            cards.filter(c => user.field_interests?.includes(`${c.drupalId}`))
          );
        }
      }
    };
    fetchInterests();
  }, []);

  useEffect(() => {
    const fetchTipoServizio = async () => {
      const res = await listEntities({
        body: {
          entity: 'taxonomy_term',
          bundle: 'tipo_servizio',
          deserializeEntities: true,
          disableStatusFilter: true,
          specificResValue: ['id', 'drupal_internal__tid', 'name'],
        },
      });
      if (res) {
        const terms = res as unknown as {
          id: string;
          drupal_internal__tid: number;
          name: string;
        }[];
        const cards = terms.map(term => ({
          interestType: term.name,
          iconType: '',
          drupalId: term.drupal_internal__tid,
          uuid: term.id,
        }));
        setTipoServizioCards(cards);
        if (user?.field_interests) {
          const fieldInterests = user.field_interests;
          setTipoServizioSelected(
            cards.filter(card => fieldInterests.includes(`${card.drupalId}`))
          );
        }
      }
    };
    fetchTipoServizio();
  }, []);

  useEffect(() => {
    const fetchAssociazione = async () => {
      const res = await listEntities({
        body: {
          entity: 'taxonomy_term',
          bundle: 'associazione',
          deserializeEntities: true,
          disableStatusFilter: true,
          specificResValue: ['id', 'drupal_internal__tid', 'name'],
        },
      });
      if (res) {
        const terms = res as unknown as {
          id: string;
          drupal_internal__tid: number;
          name: string;
        }[];
        const cards = terms.map(term => ({
          interestType: term.name,
          iconType: '',
          drupalId: term.drupal_internal__tid,
          uuid: term.id,
        }));
        setAssociazioneCards(cards);
        if (user?.field_interests) {
          const fieldInterests = user.field_interests;
          setAssociazioneSelected(
            cards.filter(card => fieldInterests.includes(`${card.drupalId}`))
          );
        }
      }
    };
    fetchAssociazione();
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Container>
        <StyledPaper height="auto" padding="50px" width="100%">
          <Stack
            justifyContent={'space-between'}
            width={'100%'}
            gap={'10px'}
          >
            <Typography>
              Seleziona i servizi che ti interessano maggiormente cliccando
              sulle icone a lato, quindi premi "Salva Modifiche". Ti terremo
              aggiornato rispetto alle tematiche di maggior interesse per te.
            </Typography>

            <Stack
              direction="row"
              gap={'16px'}
              flexWrap={'wrap'}
              width={isMobile ? '100%' : 'calc(100% - 300px)'}
              justifyContent={isMobile ? 'center' : 'flex-start'}
            >
              {interestCards.map((e, index) => (
                <InterestCard
                  key={index}
                  interest={e}
                  onSetInterests={onSetInterests}
                  onRemoveInterests={onRemoveInterests}
                  cardsSelected={cardsSelected}
                />
              ))}
            </Stack>
          </Stack>

          <Stack justifyContent={'space-between'} width={'100%'} gap={'10px'}>
            <Typography sx={{ marginTop: 5 }}>Associazione</Typography>

            <Stack
              direction="row"
              gap={'16px'}
              flexWrap={'wrap'}
              width={isMobile ? '100%' : 'calc(100% - 300px)'}
              justifyContent={isMobile ? 'center' : 'flex-start'}
            >
              {associazioneCards.map((e, index) => (
                <InterestCard
                  key={index}
                  interest={e}
                  onSetInterests={interest =>
                    setAssociazioneSelected(prev => [...prev, interest])
                  }
                  onRemoveInterests={interest =>
                    setAssociazioneSelected(prev =>
                      prev.filter(i => i.drupalId !== interest.drupalId)
                    )
                  }
                  cardsSelected={associazioneSelected}
                />
              ))}
            </Stack>
          </Stack>

          <Stack justifyContent={'space-between'} width={'100%'} gap={'10px'}>
            <Typography sx={{ marginTop: 5 }}>Tipo servizio</Typography>

            <Stack
              direction="row"
              gap={'16px'}
              flexWrap={'wrap'}
              width={isMobile ? '100%' : 'calc(100% - 300px)'}
              justifyContent={isMobile ? 'center' : 'flex-start'}
            >
              {tipoServizioCards.map((e, index) => (
                <InterestCard
                  key={index}
                  interest={e}
                  onSetInterests={interest =>
                    setTipoServizioSelected(prev => [...prev, interest])
                  }
                  onRemoveInterests={interest =>
                    setTipoServizioSelected(prev =>
                      prev.filter(i => i.drupalId !== interest.drupalId)
                    )
                  }
                  cardsSelected={tipoServizioSelected}
                />
              ))}
            </Stack>
          </Stack>
          <Stack
            flexDirection={'row'}
            justifyContent={isMobile ? 'center' : 'flex-end'}
            alignItems={'center'}
            sx={{ width: '100%' }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth={isMobile}
              type="submit"
              sx={{
                width: '144px',
                height: '36px',
                backgroundColor: '#0A2233',
                marginTop: '40px',
              }}
            >
              {i18n.common.buttons.saveChanges}
            </Button>
          </Stack>
        </StyledPaper>
      </Container>
    </FormProvider>
  );
};

export { ProfileInterests };
